import React from "react";
import { ArrowRight, ShieldAlert } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/ed7eee19-61aa-456b-86f7-01a2255b2ba0/images/2b61db511b40c394bee242881608e629d22eef1d2541e32b150025fabbd8ed89.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden border-b border-white/10"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-black/65" />
      <div className="absolute inset-0 -z-10 bg-grid opacity-40" />

      {/* Top frame line */}
      <div className="absolute top-20 md:top-24 left-0 right-0 h-px bg-white/10" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-24 md:pb-32 relative">
        <div className="flex items-center gap-3 mb-8">
          <span className="inline-flex h-2 w-2 bg-red-500 pulse-dot" />
          <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-red-400">
            Live · Algorithmic Threat Intelligence
          </span>
        </div>

        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold uppercase tracking-tighter leading-[0.92] max-w-5xl">
          The algorithms are
          <br />
          <span className="text-red-500 text-glow-red">not neutral.</span>
          <br />
          <span className="text-white/90">We expose them.</span>
        </h1>

        <p className="mt-10 max-w-2xl font-body text-base md:text-lg leading-relaxed text-gray-300">
          <span className="font-mono text-red-400">/&gt;</span> High-Risk Algorithms is the dominant brand
          for exposing predatory systems, auditing algorithmic risk, and arming businesses with the
          counter-strategy to operate in an industry that has been corrupted — and untouched — for too long.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-white transition-colors"
          >
            Request Risk Audit <ArrowRight size={16} />
          </a>
          <a
            href="#manifesto"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 hover:border-white text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] transition-colors"
          >
            <ShieldAlert size={16} /> Read the Manifesto
          </a>
        </div>

        {/* Stat strip */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
          {[
            { k: "01", v: "Audits Run", d: "1,400+" },
            { k: "02", v: "Industries", d: "27" },
            { k: "03", v: "Algorithms Mapped", d: "3.2M" },
            { k: "04", v: "Corruption Vectors", d: "Untouched" },
          ].map((s) => (
            <div
              key={s.k}
              className="border-r border-white/10 last:border-r-0 py-6 md:py-8 pr-6"
              data-testid={`hero-stat-${s.k}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-red-400">
                {s.k} / {s.v}
              </div>
              <div className="mt-3 font-heading text-3xl md:text-4xl font-bold text-white">
                {s.d}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
